import { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { Navigate } from "react-router-dom";

import { db } from "@/firebase";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

/**
 * IDEAS MODULE
 * - Developer: submit & view own ideas
 * - Admin/Super Admin: approve ideas + create ticket
 */

export default function Ideas() {
  const user = JSON.parse(localStorage.getItem("user"));
  const [ideas, setIdeas] = useState([]);

  // Idea form
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "feature",
  });

  // 🔐 Auth check
  if (!user) return <Navigate to="/login" />;

  const isAdmin = ["admin", "super_admin"].includes(user.role);

  // -------------------------
  // 🔥 STABLE REALTIME LISTENER
  // -------------------------
  useEffect(() => {
    const q = query(
      collection(db, "ideas"),
      orderBy("created_at", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      setIdeas(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    });

    return () => unsub();
  }, []);

  // -------------------------
  // 🔎 ROLE BASED VISIBILITY
  // -------------------------
  const visibleIdeas = useMemo(() => {
    if (isAdmin) return ideas;
    return ideas.filter((i) => i.created_by === user.uid);
  }, [ideas, isAdmin, user.uid]);

  // -------------------------
  // ➕ CREATE IDEA
  // -------------------------
  const handleCreateIdea = async (e) => {
    e.preventDefault();

    await addDoc(collection(db, "ideas"), {
      title: form.title,
      description: form.description,
      category: form.category,
      status: "pending",
      created_by: user.uid,
      created_by_name: user.name,
      created_by_role: user.role,
      ticket_id: null,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });

    setForm({
      title: "",
      description: "",
      category: "feature",
    });
  };

  // -------------------------
  // ✅ APPROVE IDEA (ADMIN)
  // -------------------------
  const approveIdea = async (ideaId) => {
    await updateDoc(doc(db, "ideas", ideaId), {
      status: "approved",
      updated_at: serverTimestamp(),
    });
  };

  // -------------------------
  // 🎫 CREATE TICKET FROM IDEA
  // -------------------------
  const createTicketFromIdea = async (idea) => {
    const ticketRef = await addDoc(collection(db, "tickets"), {
      title: idea.title,
      description: idea.description,
      priority: "Medium",
      status: "Open",
      type: "feature",
      created_by: idea.created_by,
      created_by_name: idea.created_by_name,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });

    await updateDoc(doc(db, "ideas", idea.id), {
      ticket_id: ticketRef.id,
      updated_at: serverTimestamp(),
    });
  };

  // -------------------------
  // 🎨 STATUS COLOR
  // -------------------------
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Ideas</h1>

        {/* ➕ CREATE IDEA */}
        <form
          onSubmit={handleCreateIdea}
          className="bg-white p-4 border rounded-lg space-y-3"
        >
          <Input
            placeholder="Idea title"
            value={form.title}
            onChange={(e) =>
              setForm((p) => ({ ...p, title: e.target.value }))
            }
            required
          />

          <Textarea
            placeholder="Describe your idea"
            rows={3}
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
            required
          />

          <Select
            value={form.category}
            onValueChange={(v) =>
              setForm((p) => ({ ...p, category: v }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new_product">New Product</SelectItem>
              <SelectItem value="existing_product">
                Existing Product
              </SelectItem>
              <SelectItem value="feature">Feature</SelectItem>
              <SelectItem value="flow">Flow / UX</SelectItem>
            </SelectContent>
          </Select>

          <Button type="submit">Submit Idea</Button>
        </form>

        {/* 📋 IDEAS LIST */}
        <div className="space-y-3">
          {visibleIdeas.map((idea) => (
            <div
              key={idea.id}
              className="border p-4 rounded-lg bg-white"
            >
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h3 className="font-semibold">{idea.title}</h3>
                  <p className="text-sm text-gray-600">
                    {idea.description}
                  </p>

                  <p className="text-xs text-gray-500 mt-1">
                    {idea.category.replace("_", " ")} • by{" "}
                    {idea.created_by_name}
                  </p>
                </div>

                <Badge className={getStatusColor(idea.status)}>
                  {idea.status}
                </Badge>
              </div>

              {/* ADMIN ACTIONS */}
              {isAdmin && (
                <div className="flex gap-2 mt-3">
                  {idea.status === "pending" && (
                    <Button
                      size="sm"
                      onClick={() => approveIdea(idea.id)}
                    >
                      Approve
                    </Button>
                  )}

                  {idea.status === "approved" && !idea.ticket_id && (
                    <Button
                      size="sm"
                      onClick={() =>
                        createTicketFromIdea(idea)
                      }
                    >
                      Create Ticket
                    </Button>
                  )}

                  {idea.ticket_id && (
                    <Badge className="bg-blue-100 text-blue-800">
                      Ticket Created
                    </Badge>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
